import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { User, Building2, Phone, Mail, MapPin, MessageSquare, Send, CheckCircle, Loader2 } from 'lucide-react';
import axios from 'axios';
import { Breadcrumb } from '../components/Breadcrumb';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

import { PHP_API } from '../config/api';

const benefits = [
  'Access to premium seed varieties',
  'Competitive dealer margins',
  'Marketing & promotional support',
  'Technical training programs',
  'Priority stock allocation',
  'Dedicated relationship manager',
];

export default function DealerInquiryPage() {
  const [formData, setFormData] = useState({
    name: '',
    company_name: '',
    phone: '',
    email: '',
    location: '',
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.company_name.trim()) newErrors.company_name = 'Company name is required';
    if (!formData.phone.trim()) newErrors.phone = 'Phone is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Invalid email format';
    if (!formData.location.trim()) newErrors.location = 'Location is required';
    if (!formData.message.trim()) newErrors.message = 'Message is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setSubmitting(true);
    try {
      await axios.post(PHP_API.inquiries, formData);
      setSubmitted(true);
      toast.success('Inquiry submitted successfully!');
    } catch (error) {
      console.error('Error submitting inquiry:', error);
      toast.error('Failed to submit inquiry. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  if (submitted) {
    return (
      <div className="page-transition min-h-screen bg-stone-50 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-2xl p-12 shadow-medium text-center max-w-md mx-4"
        >
          <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-primary" />
          </div>
          <h2 className="font-heading font-bold text-2xl text-stone-900 mb-4">
            Thank You for Your Interest!
          </h2>
          <p className="text-stone-600 mb-8">
            Your dealer inquiry has been submitted successfully. Our team will review your application 
            and get back to you within 2-3 business days.
          </p>
          <Button onClick={() => setSubmitted(false)} className="btn-primary">
            Submit Another Inquiry
          </Button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="page-transition min-h-screen bg-stone-50">
      {/* Header */}
      <section className="bg-primary py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Breadcrumb items={[{ label: 'Dealer Inquiry' }]} />
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <h1 className="font-heading font-bold text-3xl sm:text-4xl text-white mb-4">
              Become a Dreamfield Dealer
            </h1>
            <p className="text-white/90 text-lg">
              Join our network of successful dealers and bring premium seeds to farmers in your region.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-5 gap-12">
            {/* Benefits */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              className="lg:col-span-2"
            >
              <h2 className="font-heading font-bold text-2xl text-stone-900 mb-6">
                Why Partner With Us?
              </h2>
              <ul className="space-y-4">
                {benefits.map((benefit, index) => (
                  <motion.li
                    key={benefit}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-center gap-3"
                  >
                    <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="w-4 h-4 text-primary" />
                    </div>
                    <span className="text-stone-700">{benefit}</span>
                  </motion.li>
                ))}
              </ul>

              <div className="mt-10 p-6 bg-secondary/10 rounded-xl">
                <h3 className="font-heading font-semibold text-lg text-stone-900 mb-3">
                  Already a Dealer?
                </h3>
                <p className="text-stone-600 text-sm mb-4">
                  Contact your relationship manager for any queries or support needs.
                </p>
                <p className="text-stone-900 font-medium">
                  Dealer Support: +1 234 567 899
                </p>
              </div>
            </motion.div>

            {/* Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              className="lg:col-span-3"
            >
              <div className="bg-white rounded-2xl p-8 shadow-soft">
                <h2 className="font-heading font-bold text-2xl text-stone-900 mb-6">
                  Dealer Inquiry Form
                </h2>

                <form onSubmit={handleSubmit} className="space-y-6" data-testid="dealer-inquiry-form">
                  <div className="grid sm:grid-cols-2 gap-6">
                    <div>
                      <label className="form-label">
                        <User className="w-4 h-4 inline mr-2" />
                        Full Name *
                      </label>
                      <Input
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        className={`input-style ${errors.name ? 'border-red-500' : ''}`}
                        placeholder="Enter your full name"
                        data-testid="input-name"
                      />
                      {errors.name && <p className="error-text">{errors.name}</p>}
                    </div>

                    <div>
                      <label className="form-label">
                        <Building2 className="w-4 h-4 inline mr-2" />
                        Company Name *
                      </label>
                      <Input
                        name="company_name"
                        value={formData.company_name}
                        onChange={handleChange}
                        className={`input-style ${errors.company_name ? 'border-red-500' : ''}`}
                        placeholder="Enter company name"
                        data-testid="input-company"
                      />
                      {errors.company_name && <p className="error-text">{errors.company_name}</p>}
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-6">
                    <div>
                      <label className="form-label">
                        <Phone className="w-4 h-4 inline mr-2" />
                        Phone Number *
                      </label>
                      <Input
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className={`input-style ${errors.phone ? 'border-red-500' : ''}`}
                        placeholder="Enter phone number"
                        data-testid="input-phone"
                      />
                      {errors.phone && <p className="error-text">{errors.phone}</p>}
                    </div>

                    <div>
                      <label className="form-label">
                        <Mail className="w-4 h-4 inline mr-2" />
                        Email Address *
                      </label>
                      <Input
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        className={`input-style ${errors.email ? 'border-red-500' : ''}`}
                        placeholder="Enter email address"
                        data-testid="input-email"
                      />
                      {errors.email && <p className="error-text">{errors.email}</p>}
                    </div>
                  </div>

                  <div>
                    <label className="form-label">
                      <MapPin className="w-4 h-4 inline mr-2" />
                      Location / City *
                    </label>
                    <Input
                      name="location"
                      value={formData.location}
                      onChange={handleChange}
                      className={`input-style ${errors.location ? 'border-red-500' : ''}`}
                      placeholder="Enter your city and state"
                      data-testid="input-location"
                    />
                    {errors.location && <p className="error-text">{errors.location}</p>}
                  </div>

                  <div>
                    <label className="form-label">
                      <MessageSquare className="w-4 h-4 inline mr-2" />
                      Message *
                    </label>
                    <Textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      className={`input-style min-h-[120px] ${errors.message ? 'border-red-500' : ''}`}
                      placeholder="Tell us about your business and why you want to become a dealer"
                      data-testid="input-message"
                    />
                    {errors.message && <p className="error-text">{errors.message}</p>}
                  </div>

                  <Button
                    type="submit"
                    disabled={submitting}
                    className="btn-primary w-full sm:w-auto"
                    data-testid="submit-inquiry-btn"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Submit Inquiry
                      </>
                    )}
                  </Button>
                </form>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}


